'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Settings, Plus, Pencil, Trash2, DollarSign } from 'lucide-react';
import {
  useToggleCharge,
  useUpdateFixedPrice,
  useUpdatePercentagePrice,
  useUpdateWeeklyAmount,
  useUpdateRxRate,
  useAddCustomFee,
  useUpdateCustomFee,
  useToggleCustomFee,
  useDeleteCustomFee,
} from '@/lib/api/account-manager';
import type { AccountPlan, AccountPlanPricesColumn, CustomFee } from '@/lib/types/accountPlans';
import { productTypeToEnableChargeField, productTypeToPercentageField } from '@/lib/types/accountPlans';
import type { StripeProductType } from '@/lib/stripe';
import { RX_PUBLIC_SELL_BPS } from '@tagadapay/billing-core/compute/rx-fees';

interface PlanSettingsTabProps {
  // Using any to handle flexible API response - the component only uses specific fields
  accountPlan: any;
  refetch: () => void;
  /**
   * Per-field buy-rate floor (account_plans-shaped) when the merchant's
   * partner has a signed CRM buy rate; `null` for direct/legacy merchants
   * (no floor). The sell rate can never go below the matching field.
   */
  floor?: Record<string, number> | null;
}

// Mapping from StripeProductType to price field name
const productToPriceField: Record<StripeProductType, AccountPlanPricesColumn> = {
  'approved_transaction': 'amountPerSuccessTransaction',
  'approved_subclub_transaction': 'amountPerSubclubTransaction',
  'declined_transaction': 'amountPerDeclinedTransaction',
  '3ds_verification': 'amountPer3dsVerification',
  'chargeback_alert': 'amountPerChargeBack',
  'refund_processing': 'amountPerRefund',
  'late_payment': 'amountPerLatePayment',
  'apple_pay_domain': 'amountPerApplePayDomain',
  'custom_domain': 'amountPerCustomDomain',
  // These use percentage-only pricing
  'automatic_retry': 'amountPerSuccessTransaction',
  'upsell': 'amountPerSuccessTransaction',
  'subscription': 'amountPerSuccessTransaction',
  'apple_pay': 'amountPerSuccessTransaction',
  'google_pay': 'amountPerSuccessTransaction',
};

// Display names for usage types
const usageTypeNames: Record<string, string> = {
  base_plan_fee: 'Weekly Base Fee',
  approved_transaction: 'Approved Transaction',
  approved_subclub_transaction: 'SubClub Transaction',
  declined_transaction: 'Declined Transaction',
  refund_processing: 'Refund Processing',
  chargeback_alert: 'Chargeback Alert',
  '3ds_verification': '3DS Verification',
  late_payment: 'Late Payment',
  automatic_retry: 'Automatic Retries',
  upsell: 'Upsells',
  subscription: 'Subscriptions',
  apple_pay: 'Apple Pay Transactions',
  google_pay: 'Google Pay Transactions',
  apple_pay_domain: 'Apple Pay Domain Verification',
  custom_domain: 'Custom Domain',
};

// Pricing sections
const pricingSections = {
  'Core Processing': [
    'base_plan_fee' as any,
    'approved_transaction',
    'approved_subclub_transaction',
    'declined_transaction',
    'refund_processing',
    'chargeback_alert',
    '3ds_verification',
    'late_payment',
  ] as StripeProductType[],
  'Growth Add-ons': [
    'automatic_retry',
  ] as StripeProductType[],
  'Revenue Boosters': [
    'upsell',
    'subscription',
  ] as StripeProductType[],
  'Wallet Payments': [
    'apple_pay',
    'google_pay',
    'apple_pay_domain',
    'custom_domain',
  ] as StripeProductType[],
};

// Helper to check if product type has fixed pricing
const hasFixedPricing = (productType: StripeProductType): boolean => {
  return pricingSections['Core Processing'].includes(productType) || productType === 'apple_pay_domain' || productType === 'custom_domain';
};

// Format money
const formatMoney = (amount: number, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

// Format percentage from basis points
const formatPercentage = (basisPoints: number) => {
  const percentage = basisPoints / 100;
  return `${percentage.toFixed(2)}%`;
};

// Convert basis points to percentage
const basisPointsToPercentage = (basisPoints: number) => basisPoints / 100;

export function PlanSettingsTab({ accountPlan, refetch, floor }: PlanSettingsTabProps) {
  const [editingPrice, setEditingPrice] = useState<AccountPlanPricesColumn | null>(null);
  const [priceValue, setPriceValue] = useState<number>(0);
  const [editingPercentage, setEditingPercentage] = useState<StripeProductType | null>(null);
  const [percentageValue, setPercentageValue] = useState<string>('');
  const [isEditingWeeklyAmount, setIsEditingWeeklyAmount] = useState(false);
  const [weeklyAmount, setWeeklyAmount] = useState(accountPlan?.amountPlan ? accountPlan.amountPlan / 100 : 0);
  const [isEditingRxRate, setIsEditingRxRate] = useState(false);
  const [rxRateValue, setRxRateValue] = useState<string>('');
  // Inline buy-rate-floor violations (client-side clamp; the backend also
  // enforces the same floor as defense-in-depth).
  const [floorError, setFloorError] = useState<string | null>(null);

  // Custom fee dialog state
  const [showAddFeeDialog, setShowAddFeeDialog] = useState(false);
  const [editingFee, setEditingFee] = useState<CustomFee | null>(null);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeDescription, setFeeDescription] = useState('');
  const [feeIsRecurring, setFeeIsRecurring] = useState(false);
  const [deletingFeeId, setDeletingFeeId] = useState<string | null>(null);

  // Mutations
  const toggleChargeMutation = useToggleCharge(refetch);
  const updatePriceMutation = useUpdateFixedPrice(refetch);
  const updatePercentageMutation = useUpdatePercentagePrice(refetch);
  const updateWeeklyAmountMutation = useUpdateWeeklyAmount(refetch);
  const updateRxRateMutation = useUpdateRxRate(refetch);

  // Custom fee mutations
  const addCustomFeeMutation = useAddCustomFee(refetch);
  const updateCustomFeeMutation = useUpdateCustomFee(refetch);
  const toggleCustomFeeMutation = useToggleCustomFee(refetch);
  const deleteCustomFeeMutation = useDeleteCustomFee(refetch);

  if (!accountPlan) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Loading account plan...</p>
        </CardContent>
      </Card>
    );
  }

  const handleToggleCharge = (type: StripeProductType, currentValue: boolean) => {
    toggleChargeMutation.mutate({
      productType: type,
      enabled: !currentValue,
      accountId: accountPlan.accountId,
    });
  };

  const handleEditPrice = (type: StripeProductType) => {
    const priceField = productToPriceField[type];
    setFloorError(null);
    setEditingPrice(priceField);
    setPriceValue(accountPlan[priceField]);
  };

  const handleSavePrice = () => {
    if (!editingPrice) return;
    const floorCents = floor?.[editingPrice];
    if (floorCents != null && priceValue < floorCents) {
      setFloorError(
        `Cannot go below the buy rate of ${formatMoney(floorCents, accountPlan.billingCurrency)}.`,
      );
      return;
    }
    updatePriceMutation.mutate({
      priceType: editingPrice,
      amount: priceValue,
      accountId: accountPlan.accountId,
    }, {
      onSuccess: () => {
        setEditingPrice(null);
        setFloorError(null);
      },
    });
  };

  const handleEditPercentage = (type: StripeProductType) => {
    const percentageField = productTypeToPercentageField[type];
    const currentBasisPoints = accountPlan[percentageField] || 0;
    setFloorError(null);
    setEditingPercentage(type);
    setPercentageValue(basisPointsToPercentage(currentBasisPoints).toFixed(2));
  };

  const handleSavePercentage = () => {
    if (!editingPercentage) return;
    const percentage = parseFloat(percentageValue);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      return;
    }
    const percentageField = productTypeToPercentageField[editingPercentage];
    const floorBps = floor?.[percentageField];
    const basisPoints = Math.round(percentage * 100);
    if (floorBps != null && basisPoints < floorBps) {
      setFloorError(`Cannot go below the buy rate of ${formatPercentage(floorBps)}.`);
      return;
    }
    updatePercentageMutation.mutate({
      productType: editingPercentage,
      percentage: percentage,
      accountId: accountPlan.accountId,
    }, {
      onSuccess: () => {
        setEditingPercentage(null);
        setFloorError(null);
      },
    });
  };

  const handleEditWeeklyAmount = () => {
    setFloorError(null);
    setIsEditingWeeklyAmount(true);
    setWeeklyAmount(accountPlan.amountPlan / 100);
  };

  const handleSaveWeeklyAmount = () => {
    const amountInCents = Math.round(weeklyAmount * 100);
    const floorCents = floor?.amountPlan;
    if (floorCents != null && amountInCents < floorCents) {
      setFloorError(
        `Cannot go below the buy rate of ${formatMoney(floorCents, accountPlan.billingCurrency)}/week.`,
      );
      return;
    }
    updateWeeklyAmountMutation.mutate({
      accountId: accountPlan.accountId,
      amount: amountInCents,
    }, {
      onSuccess: () => {
        setIsEditingWeeklyAmount(false);
        setFloorError(null);
      },
    });
  };

  // TagadaRx rate: floor = 3.90% public price, or the partner's Rx buy rate
  // when its signed version allows selling below it (server re-checks).
  const rxFloorBps: number = floor?.pctPerRxTransaction ?? RX_PUBLIC_SELL_BPS;
  const rxRateBps: number = accountPlan.pctPerRxTransaction ?? RX_PUBLIC_SELL_BPS;

  const handleEditRxRate = () => {
    setFloorError(null);
    setIsEditingRxRate(true);
    setRxRateValue(basisPointsToPercentage(rxRateBps).toFixed(2));
  };

  const handleSaveRxRate = () => {
    const percentage = parseFloat(rxRateValue);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) return;
    if (Math.round(percentage * 100) < rxFloorBps) {
      setFloorError(`Cannot go below ${formatPercentage(rxFloorBps)}.`);
      return;
    }
    updateRxRateMutation.mutate({
      accountId: accountPlan.accountId,
      percentage,
    }, {
      onSuccess: () => {
        setIsEditingRxRate(false);
        setFloorError(null);
      },
    });
  };

  // Custom fee handlers
  const handleOpenAddFeeDialog = () => {
    setEditingFee(null);
    setFeeAmount('');
    setFeeDescription('');
    setFeeIsRecurring(false);
    setShowAddFeeDialog(true);
  };

  const handleOpenEditFeeDialog = (fee: CustomFee) => {
    setEditingFee(fee);
    setFeeAmount((fee.amountCents / 100).toString());
    setFeeDescription(fee.description);
    setFeeIsRecurring(fee.isRecurring);
    setShowAddFeeDialog(true);
  };

  const handleSaveFee = () => {
    const amount = parseFloat(feeAmount);
    if (isNaN(amount) || amount <= 0 || !feeDescription.trim()) {
      return;
    }

    if (editingFee) {
      // Update existing fee
      updateCustomFeeMutation.mutate({
        accountId: accountPlan.accountId,
        feeId: editingFee.id,
        amountDollars: amount,
        description: feeDescription.trim(),
        isRecurring: feeIsRecurring,
      }, {
        onSuccess: () => {
          setShowAddFeeDialog(false);
          setEditingFee(null);
        },
      });
    } else {
      // Add new fee
      addCustomFeeMutation.mutate({
        accountId: accountPlan.accountId,
        amountDollars: amount,
        description: feeDescription.trim(),
        isRecurring: feeIsRecurring,
      }, {
        onSuccess: () => {
          setShowAddFeeDialog(false);
        },
      });
    }
  };

  const handleToggleFee = (fee: CustomFee) => {
    toggleCustomFeeMutation.mutate({
      accountId: accountPlan.accountId,
      feeId: fee.id,
      enabled: !fee.enabled,
    });
  };

  const handleDeleteFee = (feeId: string) => {
    deleteCustomFeeMutation.mutate({
      accountId: accountPlan.accountId,
      feeId,
    }, {
      onSuccess: () => setDeletingFeeId(null),
    });
  };

  // Get custom fees from account plan
  const customFees: CustomFee[] = accountPlan.customFees || [];

  // Compact, right-aligned buy-rate floor hint. Renders only when the line
  // carries a real floor (> 0), so 0-value lines stay clean. Turns red when
  // the active edit dips below the floor.
  const floorHint = (value: number | undefined, kind: 'money' | 'pct', violated: boolean) => {
    if (value == null || value <= 0) return null;
    const label = kind === 'money' ? formatMoney(value, accountPlan.billingCurrency) : formatPercentage(value);
    return (
      <span
        className={`text-[11px] leading-none ${violated ? 'font-medium text-destructive' : 'text-muted-foreground/60'}`}
        title={violated ? floorError ?? undefined : 'Buy rate floor — price at or above, never below'}
      >
        min {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Pricing Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {Object.entries(pricingSections).map(([sectionName, sectionTypes]) => (
              <div key={sectionName}>
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                  {sectionName}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sectionTypes.map((type) => {
                    // Special handling for base_plan_fee
                    if (type === 'base_plan_fee' as any) {
                      return (
                        <Card key={type} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                              <span className="text-sm font-medium">Weekly Base Fee</span>
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              Always On
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Amount:</span>
                            {isEditingWeeklyAmount ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={weeklyAmount}
                                  onChange={(e) => setWeeklyAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                  className="w-20 h-7 text-xs"
                                  min={floor?.amountPlan != null ? floor.amountPlan / 100 : 0}
                                  step="0.01"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={handleSaveWeeklyAmount}
                                  disabled={updateWeeklyAmountMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => setIsEditingWeeklyAmount(false)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>{formatMoney(accountPlan.amountPlan, accountPlan.billingCurrency)}/week</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={handleEditWeeklyAmount}
                                >
                                  Edit
                                </Button>
                              </div>
                            )}
                          </div>

                          {(floor?.amountPlan ?? 0) > 0 && (
                            <div className="mt-1 text-right">
                              {floorHint(floor?.amountPlan, 'money', isEditingWeeklyAmount && !!floorError)}
                            </div>
                          )}

                          <p className="text-xs text-muted-foreground mt-3">
                            Charged every Monday regardless of usage
                          </p>
                        </Card>
                      );
                    }

                    const productType = type as StripeProductType;
                    const enableField = productTypeToEnableChargeField[productType];
                    const priceField = productToPriceField[productType];
                    const percentageField = productTypeToPercentageField[productType];
                    const typeEnabled = enableField ? accountPlan[enableField] : false;
                    const price = accountPlan[priceField];
                    const percentageBasisPoints = accountPlan[percentageField] || 0;
                    const isEditingFixed = editingPrice === priceField;
                    const isEditingPct = editingPercentage === productType;
                    const showFixedPricing = hasFixedPricing(productType);

                    return (
                      <Card key={type} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${typeEnabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span className="text-sm font-medium">
                              {usageTypeNames[type]}
                            </span>
                          </div>
                          <Switch
                            checked={typeEnabled}
                            onCheckedChange={() => handleToggleCharge(productType, typeEnabled)}
                            disabled={toggleChargeMutation.isPending}
                          />
                        </div>

                        {/* Fixed Amount Pricing */}
                        {showFixedPricing && (
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Fixed:</span>
                            {isEditingFixed ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={priceValue}
                                  onChange={(e) => setPriceValue(parseInt(e.target.value) || 0)}
                                  className="w-20 h-7 text-xs"
                                  min={editingPrice && floor?.[editingPrice] != null ? floor[editingPrice] : 0}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={handleSavePrice}
                                  disabled={updatePriceMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => setEditingPrice(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>
                                  {productType === 'apple_pay_domain' || productType === 'custom_domain'
                                    ? `${formatMoney(price, accountPlan.billingCurrency)}/week`
                                    : `${formatMoney(price, accountPlan.billingCurrency)}/txn`
                                  }
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => handleEditPrice(productType)}
                                  disabled={!typeEnabled}
                                >
                                  Edit
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Buy-rate floor — fixed */}
                        {showFixedPricing && (floor?.[priceField] ?? 0) > 0 && (
                          <div className="mb-2 text-right">
                            {floorHint(floor?.[priceField], 'money', isEditingFixed && !!floorError)}
                          </div>
                        )}

                        {/* Percentage Pricing */}
                        {productType !== 'apple_pay_domain' && productType !== 'custom_domain' && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Percentage:</span>
                            {isEditingPct ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="text"
                                  value={percentageValue}
                                  onChange={(e) => setPercentageValue(e.target.value)}
                                  className="w-16 h-7 text-xs"
                                  placeholder="1.25"
                                />
                                <span className="text-xs">%</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  onClick={handleSavePercentage}
                                  disabled={updatePercentageMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => setEditingPercentage(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>{formatPercentage(percentageBasisPoints)}</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => handleEditPercentage(productType)}
                                  disabled={!typeEnabled}
                                >
                                  Edit
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Buy-rate floor — percentage */}
                        {productType !== 'apple_pay_domain' && productType !== 'custom_domain' && (floor?.[percentageField] ?? 0) > 0 && (
                          <div className="mt-1 text-right">
                            {floorHint(floor?.[percentageField], 'pct', isEditingPct && !!floorError)}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Tagada Rx Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b">
                Tagada Rx
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm font-medium">Transactions TagadaRx</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      Always On
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Percentage:</span>
                    {isEditingRxRate ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={rxRateValue}
                          onChange={(e) => setRxRateValue(e.target.value)}
                          className="w-16 h-7 text-xs"
                          placeholder="3.90"
                        />
                        <span className="text-xs">%</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={handleSaveRxRate}
                          disabled={updateRxRateMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => setIsEditingRxRate(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{formatPercentage(rxRateBps)}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={handleEditRxRate}
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-1 text-right">
                    {floorHint(rxFloorBps, 'pct', isEditingRxRate && !!floorError)}
                  </div>

                  <p className="text-xs text-muted-foreground mt-3">
                    Replaces every success fee on Rx transactions. Collected at payment, never invoiced.
                  </p>
                </Card>
              </div>
            </div>

            {/* Custom Fees Section */}
            <div>
              <div className="flex items-center justify-between mb-4 pb-2 border-b">
                <h3 className="text-lg font-semibold">Custom Fees</h3>
                <Button size="sm" onClick={handleOpenAddFeeDialog}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Fee
                </Button>
              </div>
              {customFees.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">
                  No custom fees configured. Click "Add Fee" to add one.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {customFees.map((fee) => (
                    <Card
                      key={fee.id}
                      className={`p-4 gap-y-2 ${fee.enabled ? '' : 'opacity-50 bg-muted/30'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${fee.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <span className="text-sm font-medium truncate max-w-[120px]" title={fee.description}>
                            {fee.description}
                          </span>
                        </div>
                        <Switch
                          checked={fee.enabled}
                          onCheckedChange={() => handleToggleFee(fee)}
                          disabled={toggleCustomFeeMutation.isPending}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-sm">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-medium">
                          {formatMoney(fee.amountCents, accountPlan.billingCurrency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 mt-2 border-t">
                        <Badge variant={fee.isRecurring ? 'default' : 'secondary'} className="text-xs">
                          {fee.isRecurring ? 'Recurring' : 'One-time'}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleOpenEditFeeDialog(fee)}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          {deletingFeeId === fee.id ? (
                            <>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => handleDeleteFee(fee.id)}
                                disabled={deleteCustomFeeMutation.isPending}
                              >
                                Confirm
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => setDeletingFeeId(null)}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-destructive"
                              onClick={() => setDeletingFeeId(fee.id)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Fee Dialog */}
      <Dialog open={showAddFeeDialog} onOpenChange={setShowAddFeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingFee ? 'Edit Custom Fee' : 'Add Custom Fee'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="feeAmount">Amount (USD)</Label>
              <Input
                id="feeAmount"
                type="number"
                step="0.01"
                min="0.01"
                value={feeAmount}
                onChange={(e) => setFeeAmount(e.target.value)}
                placeholder="e.g., 500.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="feeDescription">Description</Label>
              <Input
                id="feeDescription"
                value={feeDescription}
                onChange={(e) => setFeeDescription(e.target.value)}
                placeholder="e.g., Setup fee, Consulting fee"
                maxLength={200}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="feeRecurring"
                checked={feeIsRecurring}
                onCheckedChange={setFeeIsRecurring}
              />
              <Label htmlFor="feeRecurring">
                Recurring (applies every week)
              </Label>
            </div>
            {!feeIsRecurring && (
              <p className="text-xs text-muted-foreground">
                One-time fees are automatically disabled after billing runs.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFeeDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveFee}
              disabled={
                addCustomFeeMutation.isPending ||
                updateCustomFeeMutation.isPending ||
                !feeAmount ||
                !feeDescription.trim()
              }
            >
              {editingFee ? 'Save Changes' : 'Add Fee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
